import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { formatINR, formatWeight } from '@vj/shared';
import { api } from '../../lib/api.js';
import { useListQuery } from '../../hooks/useListQuery.js';
import { useDebouncedValue } from '../../hooks/useDebouncedValue.js';
import { DataTable } from '../../components/ui/DataTable.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { ChitTabs } from './ChitTabs.jsx';

const STATUS_LABEL = { active: 'Active', matured: 'Matured', redeemed: 'Redeemed', defaulted: 'Defaulted', closed: 'Closed' };
const STATUS_TONE = {
  active: 'text-muted',
  matured: 'text-primary',
  redeemed: 'text-success',
  defaulted: 'text-danger',
  closed: 'text-danger',
};

const COLUMNS = [
  { key: 'scheme_no', header: 'Scheme #', sortKey: 'scheme_no' },
  { key: 'customer_name', header: 'Customer' },
  { key: 'scheme_name', header: 'Plan' },
  {
    key: 'progress',
    header: 'Progress',
    render: (r) => `${r.installments_paid} / ${r.duration_months}`,
  },
  {
    key: 'value',
    header: 'Paid to date',
    align: 'right',
    render: (r) => (r.scheme_type === 'gold' ? formatWeight(r.total_grams) : formatINR(r.total_paid)),
  },
  { key: 'maturity_date', header: 'Maturity', sortKey: 'maturity_date', render: (r) => new Date(r.maturity_date).toLocaleDateString('en-IN') },
  { key: 'status', header: 'Status', render: (r) => <span className={STATUS_TONE[r.status]}>{STATUS_LABEL[r.status]}</span> },
];

export default function AccountsListPage() {
  const [query, onQueryChange] = useListQuery({ sort: 'joining_date' });
  const debouncedSearch = useDebouncedValue(query.search, 350);
  const navigate = useNavigate();

  const listQuery = useQuery({
    queryKey: ['chit', 'accounts', { ...query, search: debouncedSearch }],
    queryFn: () => api.get('/chit/accounts', { query: { ...query, search: debouncedSearch } }),
    placeholderData: (prev) => prev,
  });

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center justify-between gap-3">
        <h1 className="font-display text-2xl font-semibold tracking-tight">Chit &amp; gold savings</h1>
        <Button onClick={() => navigate('/chit/new')}>
          <Plus size={17} aria-hidden="true" /> New account
        </Button>
      </div>

      <ChitTabs />

      <DataTable
        columns={COLUMNS}
        data={listQuery.data?.data ?? []}
        meta={listQuery.data?.meta}
        query={query}
        onQueryChange={onQueryChange}
        isLoading={listQuery.isLoading}
        onRowClick={(row) => navigate(`/chit/${row.id}`)}
        searchPlaceholder="Search by scheme number, customer or mobile…"
        emptyLabel="No chit accounts yet."
      />
    </div>
  );
}
